'use client';

import React, { ReactNode, useState } from 'react';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { CardsSlider } from '@/blocks/components/CardsSlider';
import { Container } from '@/blocks/components/Container';
import { Disclaimer } from '@/blocks/components/Disclaimer';
import { Masonry2x2 } from '@/blocks/components/Masonry2x2';
import Masonry2x2Card from '@/blocks/components/Masonry2x2/Masonry2x2Card';
import { LinkSignup } from '@/blocks/components/PlatformLink';
import { CenterHeading, VStack } from '@/blocks/components/layout';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import buttons from '@/styles/button.module.scss';

import { AnimatedTabButtons } from './AnimatedTabButtons';
import { StaggerTabContent } from './StaggerTabContent';
import communityIcon from './assets/community.png';
import diversifyIcon from './assets/diversify.png';
import earnFollowerIcon from './assets/earn-follower.png';
import earnProviderIcon from './assets/earn-provider.png';
import easyPlaceIcon from './assets/easy-place.png';
import feesIcon from './assets/fees.png';
import manageRiskIcon from './assets/manage-risk.png';
import reputationIcon from './assets/reputation.png';
import styles from './styles.module.scss';
import SvgStarComponent from './svg-star-component/svg-star';

export const AnimatedTabs = () => {
  const tr = useTranslations();
  const bp = useBreakpoint();
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const onTabClick = (index: number) => {
    setActiveTabIndex(index);
  };

  const cardsDataFollower: {
    image: any;
    title: ReactNode;
    description: ReactNode;
  }[] = [
    {
      image: earnFollowerIcon,
      title: tr.rich('pages.copyTrading.sections.earn.follower.cards.0.title'),
      description: tr.rich('pages.copyTrading.sections.earn.follower.cards.0.description'),
    },
    {
      image: diversifyIcon,
      title: tr.rich('pages.copyTrading.sections.earn.follower.cards.1.title'),
      description: tr.rich('pages.copyTrading.sections.earn.follower.cards.1.description'),
    },
    {
      image: easyPlaceIcon,
      title: tr.rich('pages.copyTrading.sections.earn.follower.cards.2.title'),
      description: tr.rich('pages.copyTrading.sections.earn.follower.cards.2.description'),
    },
    {
      image: manageRiskIcon,
      title: tr.rich('pages.copyTrading.sections.earn.follower.cards.3.title'),
      description: tr.rich('pages.copyTrading.sections.earn.follower.cards.3.description'),
    },
  ];

  const cardsDataProvider: {
    image: any;
    title: any;
    description: any;
  }[] = [
    {
      image: earnProviderIcon,
      title: tr.rich('pages.copyTrading.sections.earn.manager.cards.0.title'),
      description: tr.rich('pages.copyTrading.sections.earn.manager.cards.0.description'),
    },
    {
      image: reputationIcon,
      title: tr.rich('pages.copyTrading.sections.earn.manager.cards.1.title'),
      description: tr.rich('pages.copyTrading.sections.earn.manager.cards.1.description'),
    },
    {
      image: communityIcon,
      title: tr.rich('pages.copyTrading.sections.earn.manager.cards.2.title'),
      description: tr.rich('pages.copyTrading.sections.earn.manager.cards.2.description'),
    },
    {
      image: feesIcon,
      title: tr.rich('pages.copyTrading.sections.earn.manager.cards.3.title'),
      description: tr.rich('pages.copyTrading.sections.earn.manager.cards.3.description'),
    },
  ];

  const tabs = [
    {
      label: tr.rich('pages.copyTrading.sections.earn.switcher.follower.btn'),
      icon: <SvgStarComponent />,
      content: cardsDataFollower,
    },
    {
      label: tr.rich('pages.copyTrading.sections.earn.switcher.manager.btn'),
      icon: <SvgStarComponent />,
      content: cardsDataProvider,
    },
  ];

  return (
    <VStack>
      <Container>
        <AnimatedTabButtons tabs={tabs} activeTabIndex={activeTabIndex} onTabClick={onTabClick} />
      </Container>

      {bp === 'mobile' && (
        <CardsSlider
          key={activeTabIndex}
          renderCard={(card) => <Masonry2x2Card card={card} />}
          items={activeTabIndex === 0 ? cardsDataFollower : cardsDataProvider}
          swiperProps={{ loop: false, rewind: true }}
        />
      )}
      {bp !== 'mobile' && (
        <Container>
          <div>
            {tabs.map((tab, index) => (
              <StaggerTabContent key={String(tab.label)} isActive={index === activeTabIndex}>
                <Masonry2x2 cardsData={tab.content} />
              </StaggerTabContent>
            ))}
          </div>
        </Container>
      )}

      <Container>
        <CenterHeading>
          <LinkSignup
            className={classNames(buttons.btn, buttons.btnPrimary, buttons.btnM, styles.actionButton)}
            data-event-label={
              activeTabIndex === 0
                ? tr.rich('en.pages.copyTrading.sections.earn.switcher.follower.actionBtn')
                : tr.rich('en.pages.copyTrading.sections.earn.switcher.manager.actionBtn')
            }
          >
            {activeTabIndex === 0
              ? tr.rich('pages.copyTrading.sections.earn.switcher.follower.actionBtn')
              : tr.rich('pages.copyTrading.sections.earn.switcher.manager.actionBtn')}
          </LinkSignup>
          <Disclaimer>{tr.rich('pages.copyTrading.altTradingRisks')}</Disclaimer>
        </CenterHeading>
      </Container>
    </VStack>
  );
};
